'use client';

import { useState, useEffect, useRef } from 'react';
import { VolumeX, Volume2 } from 'lucide-react';
import { useMultiClick } from '@/hooks/useMultiClick';
import MediaUploadModal from '@/components/MediaUploadModal';

const Hero = (): JSX.Element => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasBeenUnmuted, setHasBeenUnmuted] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleMultiClick = useMultiClick(5, () => setShowUploadModal(true));

  // Auto-start video when loaded
  useEffect(() => {
    if (isVideoLoaded && videoRef.current) {
      videoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch((error) => {
        console.log('Auto-play failed:', error);
      });
    }
  }, [isVideoLoaded]);

  const handleVideoClick = () => {
    handleMultiClick();
    if (videoRef.current) {
      if (isMuted && !hasBeenUnmuted) {
        videoRef.current.muted = false;
        setIsMuted(false);
        setHasBeenUnmuted(true);
      } else {
        if (isVideoPlaying) {
          videoRef.current.pause();
          setIsVideoPlaying(false);
        } else {
          videoRef.current.play();
          setIsVideoPlaying(true);
        }
      }
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
      if (!isMuted) {
        setHasBeenUnmuted(true);
      }
    }
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
  };

  const handleVideoLoadedData = () => {
    setIsVideoLoaded(true);
  };

  return (
    <section id="home" className="relative min-h-screen h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0 w-full h-full">
        <video
          ref={videoRef}
          className="w-full h-full object-cover cursor-pointer min-h-screen"
          style={{
            minHeight: '100vh',
            minWidth: '100vw',
            objectFit: 'cover',
            objectPosition: 'center center'
          }}
          muted={isMuted}
          playsInline
          preload="metadata"
          onEnded={handleVideoEnded}
          onLoadedData={handleVideoLoadedData}
          onClick={handleVideoClick}
        
        >
          <source
            src="https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/Untitled%20design%20(21).mp4"
            type="video/mp4"
          />
          {/* Fallback image if video fails to load */}
          <div 
            className="w-full h-full bg-cover bg-center bg-no-repeat min-h-screen"
            style={{
              backgroundImage: `url('https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop')`,
              minHeight: '100vh',
              minWidth: '100vw'
            }}
          />
        </video>
      </div>

      {/* Mute/Unmute Button */}
      {isVideoLoaded && (
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-40">
          <button
            onClick={toggleMute}
            className="bg-black/50 backdrop-blur-sm text-white p-3 sm:p-4 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-110 shadow-lg"
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          >
            {isMuted ? (
              <VolumeX className="h-5 w-5 sm:h-6 sm:w-6" />
            ) : (
              <Volume2 className="h-5 w-5 sm:h-6 sm:w-6" />
            )}
          </button>
        </div>
      )}

      {/* Video Status Indicator */}
      {isVideoLoaded && (
        <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 z-40 bg-black/50 backdrop-blur-sm text-white px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium max-w-[200px] sm:max-w-none">
          <div className="flex items-center space-x-2">
            {isVideoPlaying ? (
              <>
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span>
                  {isMuted && !hasBeenUnmuted 
                    ? 'Tap for Sound' 
                    : 'Tap to Pause'
                  }
                </span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span>Tap to Play</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Wave transition to About section */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10" style={{ marginBottom: '-2px' }}>
        <svg 
          className="relative block w-full h-20 sm:h-28" 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          <defs>
            <linearGradient id="heroWaveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="transparent" stopOpacity="0" />
              <stop offset="20%" stopColor="#F9F6F2" stopOpacity="0.4" />
              <stop offset="40%" stopColor="#F9F6F2" stopOpacity="0.6" />
              <stop offset="60%" stopColor="#F9F6F2" stopOpacity="0.8" />
              <stop offset="80%" stopColor="#F9F6F2" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
            <radialGradient id="heroWaveRadial1" cx="50%" cy="50%" r="70%">
              <stop offset="0%" stopColor="#F9F6F2" stopOpacity="1" />
              <stop offset="25%" stopColor="#F9F6F2" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#F9F6F2" stopOpacity="0.6" />
              <stop offset="75%" stopColor="#F9F6F2" stopOpacity="0.3" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="heroWaveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="transparent" stopOpacity="0" />
              <stop offset="30%" stopColor="#F9F6F2" stopOpacity="0.5" />
              <stop offset="60%" stopColor="#F9F6F2" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
            <radialGradient id="heroWaveRadial2" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="#F9F6F2" stopOpacity="1" />
              <stop offset="30%" stopColor="#F9F6F2" stopOpacity="0.8" />
              <stop offset="60%" stopColor="#F9F6F2" stopOpacity="0.4" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="heroWaveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="transparent" stopOpacity="0" />
              <stop offset="40%" stopColor="#F9F6F2" stopOpacity="0.6" />
              <stop offset="70%" stopColor="#F9F6F2" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
            <radialGradient id="heroWaveRadial3" cx="50%" cy="50%" r="80%">
              <stop offset="0%" stopColor="#F9F6F2" stopOpacity="1" />
              <stop offset="40%" stopColor="#F9F6F2" stopOpacity="0.8" />
              <stop offset="80%" stopColor="#F9F6F2" stopOpacity="0.4" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="heroWaveGradient4" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="transparent" stopOpacity="0" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
          </defs>
          
          {/* Base wave with radial gradient overlay */}
          <path 
            d="M0,60L48,65C96,70,192,80,288,75C384,70,480,50,576,45C672,40,768,50,864,60C960,70,1056,80,1152,75C1200,70,1248,50,1296,45L1344,40L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#heroWaveGradient1)"
          />
          <path 
            d="M0,60L48,65C96,70,192,80,288,75C384,70,480,50,576,45C672,40,768,50,864,60C960,70,1056,80,1152,75C1200,70,1248,50,1296,45L1344,40L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#heroWaveRadial1)"
          />
          
          {/* Second wave layer with radial gradient */}
          <path 
            d="M0,30L48,35C96,40,192,50,288,45C384,40,480,20,576,15C672,10,768,20,864,30C960,40,1056,50,1152,45C1200,40,1248,20,1296,15L1344,10L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#heroWaveGradient2)"
          />
          <path 
            d="M0,40L48,45C96,50,192,60,288,55C384,50,480,30,576,25C672,20,768,30,864,40C960,50,1056,60,1152,55C1200,50,1248,30,1296,25L1344,20L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#heroWaveRadial2)"
          />
          
          {/* Third wave with radial gradient */}
          <path 
            d="M0,10L48,15C96,20,192,30,288,25C384,20,480,5,576,3C672,1,768,5,864,10C960,20,1056,30,1152,25C1200,20,1248,5,1296,3L1344,1L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#heroWaveGradient3)"
          />
          <path 
            d="M0,20L48,25C96,30,192,40,288,35C384,30,480,10,576,5C672,0,768,10,864,20C960,30,1056,40,1152,35C1200,30,1248,10,1296,5L1344,0L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#heroWaveRadial3)"
          />
          
          {/* Fourth wave */}
          <path 
            d="M0,0L48,2C96,5,192,10,288,8C384,5,480,0,576,0C672,0,768,0,864,0C960,5,1056,10,1152,8C1200,5,1248,0,1296,0L1344,0L1344,125L1296,125C1248,125,1152,125,1056,125C960,125,864,125,768,125C672,125,576,125,480,125C384,125,288,125,192,125C96,125,48,125,24,125L0,125Z" 
            fill="url(#heroWaveGradient4)"
          />
        </svg>
      </div>

      <MediaUploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} />
    </section>
  );
};

export default Hero;